// ============================================================
// ActivityViewContent.tsx
// The Activity DETAIL view's CONTENT, as a hook — shared by:
//   • ActivityViewModal (the standalone modal: these pieces → ModalFrame), and
//   • CaseQuickViewModal's in-frame drill-in (the same pieces rendered inside
//     the quick-view's own frame, under its breadcrumb header).
// One source, so the drill-in is the REAL view (permissions, file open,
// re-price, lock notes), never a copy. Everything below is the modal's
// former body, unchanged.
//
// The Activity DETAIL view — opened from a timeline entry (row / title
// click, or the eye icon) for everyone. VIEW-FIRST: Edit is a deliberate
// second step (this footer's Edit, or the feed's hover pencil).
//
// Redesigned to the approved mockup (outputs/casevo-activity-picker-and-
// view.html §4) on the shared ModalFrame — the same frame as the add-
// activity / email modals:
//   • hero band: type pill (+icon) · case ref · Billable · Unbilled WIP /
//     Billed pills · the action as title · who + when;
//   • a details card (With · Case · Logged), a Billing card (time · units ·
//     £ · grade/rate or status) and the note;
//   • footer: Delete (left) · Close · Edit (the one green primary).
// Rules (unchanged):
//   ★ GRADES ARE ADMIN-ONLY — grade + hourly rate render only for admins
//     (the API also strips them for non-admins: activityController forViewer).
//   ★ A colleague's £ withheld by the server (amountPence === null, "See
//     colleagues' billing") shows the lock + dash (WithheldAmount).
//   ★ Edit / Delete only when the caller says the viewer may (author or
//     admin) AND the entry isn't billed (the API 409s edits on billed rows).
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import WithheldAmount from './WithheldAmount'
import { billableUnits } from '../../utils/billingUnits'
import { HERO_PILL_SOFT } from './ModalFrame'
import type { Activity } from '../../types/activity.types'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import { entryTypeFor } from '../../constants/entryTypes'
import { EntryIconSvg, UiIconSvg } from '../clients/activityIcons'
import { BTN_PRIMARY } from '../../styles/buttons'
import ConfirmDialogue from './ConfirmDialogue'
import { loadCurrentGradeRates, perHour, repriceOffer } from './repriceRule'
import { repriceActivitiesApi, requestDownloadUrlFlatApi, type RepriceResult } from '../../api/casevoApi'
import { openFileById, triggerBlobDownload } from '../../utils/documentAccess'

export interface ActivityViewProps {
  // False → nothing to show (the hook returns null; its state resets).
  isOpen: boolean
  onClose: () => void
  activity: Activity | null
  // Admin → grade + hourly rate are shown. Everyone else: time/units/£ only.
  isAdmin?: boolean
  // Author or admin → footer Edit + Delete (hidden when the entry is billed).
  canModify?: boolean
  onEdit?: (a: Activity) => void
  onDelete?: (a: Activity) => void
  // Resolved case name + reference for the entry's case (when linked).
  caseLabel?: { name: string; ref: string } | null
  // Admin re-price (current grade rate) succeeded — the caller reloads.
  onRepriced?: () => void
}

// Human duration from raw seconds: "0m" / "36m" / "1h" / "1h 30m".
const formatDurationHuman = (seconds: number): string => {
  const totalMin = Math.round(seconds / 60)
  if (totalMin <= 0) return '0m'
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}
const fmtWhen = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

const CARD = 'bg-casevo-surface border border-casevo-border rounded-[14px]'
const BTN = 'inline-flex items-center gap-[7px] h-9 px-[14px] rounded-[10px] text-[13.5px] font-medium border transition-colors focus:outline-none casevo-focus'
const CHIP = 'inline-flex items-center gap-1.5 h-6 px-2 rounded-[7px] bg-casevo-mint text-[12px] font-semibold text-casevo-brand dark:text-[#8fd6b0] whitespace-nowrap'

// The frame-agnostic pieces: hero (eyebrow / pills / title / subtitle), the
// body cards and the footer's left / right groups. closeRef = the footer's
// Close button (the modal's initial focus).
export interface ActivityViewParts {
  eyebrow: React.ReactNode
  eyebrowIcon: React.ReactNode
  pills: React.ReactNode
  title: React.ReactNode
  subtitle: React.ReactNode
  closeRef: React.RefObject<HTMLButtonElement | null>
  footerLeft: React.ReactNode
  footerRight: React.ReactNode
  body: React.ReactNode
}

export const useActivityView = ({ isOpen, onClose, activity, isAdmin = false, canModify = false, onEdit, onDelete, caseLabel = null, onRepriced }: ActivityViewProps): ActivityViewParts | null => {
  const closeRef = useRef<HTMLButtonElement>(null)
  // ★ Admin per-entry RE-PRICE at the current grade rate (6-minute units,
  // rounded up). Shown ONLY when the entry's frozen rate differs from its
  // grade's current rate (repriceRule.ts), with a "Rate changed: £X/hr →
  // £Y/hr" hint. Click → preview (dryRun) → a confirm showing the old and new
  // £ → apply via the admin-only POST /api/activities/reprice (server
  // re-checks admin + unbilled — an issued bill's entries are billed — and
  // audit-logs the change). Same action as Admin → Grade rates, for one entry.
  const [reprice, setReprice] = useState<{ preview: RepriceResult | null; busy: boolean; error: string | null; forId: string } | null>(null)
  // ★ (Audit) Drop any re-price preview/error when the modal closes or shows a
  // different entry, and ignore a dry-run that resolves after that — otherwise
  // reopening the SAME entry popped the old confirm straight away.
  const liveKeyRef = useRef<string | null>(null)
  // Current grade rates (admins only — the endpoint is admin-only), to decide
  // whether Re-price would change anything.
  const [currentRates, setCurrentRates] = useState<Record<string, number> | null>(null)
  useEffect(() => {
    if (!isOpen || !isAdmin || !onRepriced) return
    let live = true
    loadCurrentGradeRates().then((r) => { if (live) setCurrentRates(r) }).catch(() => { /* no rates → no Re-price offered */ })
    return () => { live = false }
  }, [isOpen, isAdmin, !!onRepriced]) // eslint-disable-line react-hooks/exhaustive-deps
  liveKeyRef.current = isOpen && activity ? activity.id : null
  useEffect(() => { if (!isOpen) setReprice(null) }, [isOpen])
  // The linked document (e.g. the exact bill PDF that was emailed): opened via
  // the same case-access-checked signed URL as the Documents list.
  const [fileError, setFileError] = useState<string | null>(null)
  useEffect(() => { setFileError(null) }, [isOpen, activity?.id])
  // ★ Live actor name by id (fallback: the stored actorName snapshot).
  const liveName = useLiveUserName()
  if (!isOpen || !activity) return null

  const def = entryTypeFor(activity.actionType)
  const who = liveName(activity.actorUserId, activity.actorName)
  // 6-minute units rounded up — the rule the server charges (entries created
  // before that rule keep their stored exact-seconds £).
  const units = billableUnits(activity.durationSeconds)
  // (Batch 4) null → a colleague's £ withheld by the server (lock + dash).
  const charge: React.ReactNode = activity.amountPence == null ? <WithheldAmount size={13} /> : `£${(activity.amountPence / 100).toFixed(2)}`
  const billed = activity.billed === true
  // Billable only on a case; case-less time is a saved, non-billable record.
  const billableHere = def.bills && !!activity.caseId
  const caselessTime = def.bills && !activity.caseId && activity.durationSeconds > 0
  const showRate = isAdmin && (activity.gradeAtEntry != null || activity.hourlyRatePenceAtEntry != null)
  const mayModify = canModify && !billed
  const offer = onRepriced
    ? repriceOffer({ isAdmin, billed, billable: def.bills, durationSeconds: activity.durationSeconds, gradeAtEntry: activity.gradeAtEntry, frozenRatePence: activity.hourlyRatePenceAtEntry, currentRates })
    : { show: false as const }
  const canReprice = offer.show
  const repriceState = reprice && reprice.forId === activity.id ? reprice : null
  const startReprice = (): void => {
    const forId = activity.id
    setReprice({ preview: null, busy: true, error: null, forId })
    repriceActivitiesApi({ activityIds: [forId], dryRun: true })
      .then((p) => {
        if (liveKeyRef.current !== forId) return
        const gradeAtZero = !!activity.gradeAtEntry && p.zeroRateGrades.includes(activity.gradeAtEntry)
        const why = gradeAtZero
          ? `Grade ${activity.gradeAtEntry}’s current rate is £0 — set it in Admin → Grade rates first.`
          : 'Already priced at the current rate — nothing to change.'
        setReprice({ preview: p, busy: false, error: p.count === 0 ? why : null, forId })
      })
      .catch((err) => { if (liveKeyRef.current === forId) setReprice({ preview: null, busy: false, error: err instanceof Error ? err.message : 'Could not preview the re-price', forId }) })
  }
  const applyReprice = async (): Promise<void> => {
    const forId = activity.id
    setReprice((r) => (r ? { ...r, busy: true } : r))
    try {
      await repriceActivitiesApi({ activityIds: [forId] })
      setReprice(null)
      onRepriced?.()
    } catch (err) {
      setReprice((r) => (r ? { ...r, busy: false, error: err instanceof Error ? err.message : 'Re-pricing failed' } : r))
    }
  }
  const fileErr = (err: unknown, fallback: string): string =>
    (err as { response?: { data?: { error?: string } } })?.response?.data?.error || (err instanceof Error && err.message) || fallback
  const openFile = (fileId: string): void => {
    setFileError(null)
    openFileById(fileId)
      .catch((err) => setFileError(fileErr(err, 'Couldn’t open that document')))
  }
  const downloadFile = (fileId: string): void => {
    setFileError(null)
    requestDownloadUrlFlatApi(fileId)
      .then(({ downloadUrl, filename }) => triggerBlobDownload(downloadUrl, filename))
      .catch((err) => setFileError(fileErr(err, 'Couldn’t download that document')))
  }
  const fileSize = (b: number): string => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`)
  const sentEmail = activity.emailSubject != null
    ? { subject: activity.emailSubject, body: activity.emailBody ?? '', to: activity.emailTo ?? [], cc: activity.emailCc ?? [] }
    : null
  const gbp = (p: number): string => `£${(p / 100).toFixed(2)}`
  const edited = activity.updatedAt && Math.abs(new Date(activity.updatedAt).getTime() - new Date(activity.createdAt).getTime()) > 60_000

  const pills = (
    <>
      {caseLabel && <span className={HERO_PILL_SOFT}>{caseLabel.ref}</span>}
      {/* Billable only when linked to a case (every bill draws its WIP from one case). */}
      <span className={`${HERO_PILL_SOFT} !font-sans`} title={caselessTime ? 'Not linked to a case, so this time isn’t billable — edit it and link a case to bill it' : undefined}>{billableHere ? 'Billable' : caselessTime ? 'Not billable — no case' : 'Non-billable'}</span>
      {billableHere && (
        <span className={`${HERO_PILL_SOFT} !font-sans gap-1`}>
          {billed ? <><UiIconSvg icon="lock" size={11} />Billed</> : 'Unbilled WIP'}
        </span>
      )}
    </>
  )

  const row = (label: string, children: React.ReactNode): React.ReactNode => (
    <div className="flex items-center gap-[14px] px-4 min-h-[46px] py-2 border-t border-casevo-border-soft first:border-t-0">
      <span className="w-16 shrink-0 text-[10.5px] font-semibold tracking-[0.16em] uppercase text-casevo-text-soft">{label}</span>
      <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-3 gap-y-1 text-[14px] text-casevo-forest">{children}</div>
    </div>
  )
  const stat = (label: string, value: React.ReactNode, small = false): React.ReactNode => (
    <div className="min-w-0">
      <div className="text-[12px] text-casevo-text-soft">{label}</div>
      <div className={`${small ? 'text-[13.5px] leading-snug' : 'text-[17px]'} font-semibold text-casevo-forest tabular-nums mt-0.5`}>{value}</div>
    </div>
  )

  // A sent email is the record of what was sent — the server refuses to delete
  // it (409 SENT_EMAIL_RECORD), so no Delete is offered (its time stays editable).
  const mayDelete = mayModify && !!onDelete && activity.actionType !== 'email'
  const footerLeft: React.ReactNode = mayDelete || canReprice ? (
        <>
          {mayDelete && onDelete && (
            <button type="button" onClick={() => onDelete(activity)} className={`${BTN} border-casevo-border bg-casevo-surface text-casevo-danger hover:border-red-300 dark:hover:border-red-400/60`}>
              <UiIconSvg icon="trash" size={14} />Delete
            </button>
          )}
          {canReprice && (
            <button type="button" onClick={startReprice} disabled={repriceState?.busy} title="Admin: re-price this entry at the current rate for its grade" className={`${BTN} border-casevo-border bg-casevo-surface text-casevo-mid hover:border-casevo-mid disabled:opacity-60`}>
              <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></svg>
              Re-price
            </button>
          )}
          {offer.show && <span className="text-[12px] text-casevo-text-soft tabular-nums">{offer.hint}</span>}
        </>
      ) : undefined
  const footerRight: React.ReactNode = (
        <>
          <button ref={closeRef} type="button" onClick={onClose} className={`${BTN} border-casevo-border bg-casevo-surface text-casevo-mid hover:border-casevo-mid`}>
            Close
          </button>
          {mayModify && onEdit && (
            <button type="button" onClick={() => onEdit(activity)} className={`${BTN_PRIMARY} h-9 px-[14px] rounded-[10px] text-[13.5px] border border-casevo-primary`}>
              <UiIconSvg icon="edit" size={14} />{activity.actionType === 'email' ? 'Edit time' : 'Edit'}
            </button>
          )}
        </>
  )

  const body: React.ReactNode = (
    <>
      {/* Details card */}
      <div className={CARD}>
        {(activity.correspondentType || activity.correspondentName) && row('With', (
          <>
            {activity.correspondentType && <span className={CHIP}><UiIconSvg icon="user" size={12} />{activity.correspondentType}</span>}
            {activity.correspondentName && <span>{activity.correspondentName}</span>}
          </>
        ))}
        {row('Case', caseLabel ? (
          <>
            <span className={CHIP}><UiIconSvg icon="case" size={12} />{caseLabel.name}</span>
            <span className="font-mono text-[12px] text-casevo-text-soft">{caseLabel.ref}</span>
          </>
        ) : <span className="text-casevo-text-soft">Client-level</span>)}
        {row('Logged', (
          <>
            <span>{who}</span>
            <span className="text-casevo-text-soft">
              {new Date(activity.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              {edited ? ` · edited ${fmtTime(activity.updatedAt)}` : ''}
            </span>
          </>
        ))}
        {activity.attachment && row('File', (
          <>
            <button type="button" onClick={() => openFile(activity.attachment!.id)} title="Open" className="inline-flex items-center gap-1.5 min-w-0 text-left font-medium text-casevo-brand dark:text-[#8fd6b0] hover:underline focus:outline-none casevo-focus rounded-[4px]">
              <UiIconSvg icon="eye" size={13} /><span className="truncate">{activity.attachment.filename}</span>
            </button>
            <span className="text-[12px] text-casevo-text-soft">{fileSize(activity.attachment.sizeBytes)}</span>
            <button type="button" onClick={() => downloadFile(activity.attachment!.id)} aria-label={`Download ${activity.attachment.filename}`} title="Download" className="ml-auto w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand transition-colors focus:outline-none casevo-focus">
              <UiIconSvg icon="dl" size={14} />
            </button>
          </>
        ))}
      </div>
      {fileError && <div role="alert" className="-mt-1 text-[12.5px] text-casevo-danger">{fileError}</div>}

      {/* Message sent — the email exactly as the recipient got it (Send bill).
          Its own section: never folded into the free-text note. */}
      {sentEmail && (
        <div className={`${CARD} overflow-hidden`}>
          <div className="flex items-center gap-2 px-4 py-[10px] bg-casevo-input border-b border-casevo-border-soft text-[10.5px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft">
            <EntryIconSvg icon={def.icon} size={12} />Message sent
          </div>
          <dl className="m-0">
            {([['To', sentEmail.to.join(', ')], ...(sentEmail.cc.length ? [['Cc', sentEmail.cc.join(', ')]] : []), ['Subject', sentEmail.subject]] as [string, string][]).map(([k, v]) => (
              <div key={k} className="flex gap-[14px] px-4 py-2 border-b border-casevo-border-soft">
                <dt className="w-16 shrink-0 pt-px text-[10.5px] font-semibold tracking-[0.16em] uppercase text-casevo-text-soft">{k}</dt>
                <dd className={`m-0 flex-1 min-w-0 break-words text-[14px] text-casevo-forest ${k === 'Subject' ? 'font-semibold' : ''}`}>{v}</dd>
              </div>
            ))}
          </dl>
          <p className="m-0 px-5 py-4 text-[14px] leading-[1.6] text-casevo-forest whitespace-pre-wrap break-words">{sentEmail.body}</p>
        </div>
      )}

      {/* Billing card — billable types only */}
      {def.bills && (
        <div className={`${CARD} px-4 py-3`}>
          <div className="flex items-center gap-2 mb-[10px] text-[13.5px]">
            <span className="text-casevo-brand dark:text-[#8fd6b0]" aria-hidden="true">
              <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 7a4 4 0 0 0-7.5-2c-.6 1-.5 2.6-.5 4v11" /><path d="M6 20h12M6 13h9" /></svg>
            </span>
            <b className="font-semibold text-casevo-forest">Billing</b>
            <span className="ml-auto text-[12px] text-casevo-text-soft">1 unit = 6 minutes</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-[10px] rounded-[11px] bg-casevo-input px-[14px] py-3">
            {stat('Time', formatDurationHuman(activity.durationSeconds))}
            {stat('Units', units)}
            {stat('Charge', charge)}
            {showRate
              ? stat('Grade · rate', <>
                  {activity.gradeAtEntry ? `Grade ${activity.gradeAtEntry}` : '—'}
                  {activity.hourlyRatePenceAtEntry != null && <><br />£{(activity.hourlyRatePenceAtEntry / 100).toFixed(2)}/h</>}
                </>, true)
              : stat('Status', billed
                ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]"><UiIconSvg icon="lock" size={11} />Billed</span>
                : caselessTime
                  ? <span className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#fdecec] text-casevo-danger dark:bg-[rgba(127,29,29,0.35)] dark:text-[#fca5a5]">Not billable — no case</span>
                  : <span className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full bg-casevo-border-soft text-casevo-text-soft">Unbilled WIP</span>, true)}
          </div>
        </div>
      )}

      {/* Note */}
      {activity.content && (
        <div className={`${CARD} overflow-hidden`}>
          <div className="px-4 py-[10px] bg-casevo-input border-b border-casevo-border-soft text-[10.5px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft">Note</div>
          <p className="m-0 px-5 py-4 text-[14px] leading-[1.6] text-casevo-forest whitespace-pre-wrap">{activity.content}</p>
        </div>
      )}

      {repriceState?.error && (
        <div role="status" className="text-[12.5px] text-casevo-danger">{repriceState.error}</div>
      )}
      <ConfirmDialogue
        isOpen={!!repriceState?.preview && repriceState.preview.count > 0 && !repriceState.error}
        title="Re-price this entry"
        message={`Re-price at the current Grade ${activity.gradeAtEntry ?? ''} rate (6-minute units, rounded up). The change is recorded in the audit log.`}
        confirmLabel={repriceState?.busy ? 'Re-pricing…' : 'Re-price'}
        tone="primary"
        confirmDisabled={repriceState?.busy}
        onConfirm={() => { void applyReprice() }}
        onCancel={() => setReprice(null)}
      >
        {repriceState?.preview && (
          <div className="grid grid-cols-2 gap-[10px] rounded-[10px] border border-casevo-border-soft bg-casevo-input px-[12px] py-[10px] tabular-nums">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-casevo-text-soft">Now</div>
              <div className="text-[17px] font-semibold text-casevo-forest">{gbp(repriceState.preview.fromPence)}</div>
              {offer.show && <div className="text-[12px] text-casevo-text-soft">{perHour(offer.fromRatePence)}</div>}
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-casevo-text-soft">After re-price</div>
              <div className="text-[17px] font-semibold text-casevo-forest">{gbp(repriceState.preview.toPence)}</div>
              {offer.show && <div className="text-[12px] text-casevo-text-soft">{perHour(offer.toRatePence)}</div>}
            </div>
          </div>
        )}
      </ConfirmDialogue>

      {/* Quiet rule notes */}
      {(billed || (def.bills && !isAdmin) || (!canModify && !billed) || activity.actionType === 'email') && (
        <div className="flex flex-col gap-1.5">
          {billed && (
            <div className="flex gap-[7px] items-start text-[12.5px] text-casevo-text-soft"><UiIconSvg icon="lock" size={14} />This entry has been billed and is locked for editing.</div>
          )}
          {activity.actionType === 'email' && !billed && (
            <div className="flex gap-[7px] items-start text-[12.5px] text-casevo-text-soft"><UiIconSvg icon="lock" size={14} />A sent email is the record of what was sent — its content can’t be edited; only the time can be corrected.</div>
          )}
          {!canModify && !billed && (
            <div className="flex gap-[7px] items-start text-[12.5px] text-casevo-text-soft"><UiIconSvg icon="lock" size={14} />Logged by {who}. You can view it but not change it.</div>
          )}
          {def.bills && !isAdmin && (
            <div className="flex gap-[7px] items-start text-[12.5px] text-casevo-text-soft"><UiIconSvg icon="lock" size={14} />Grade and hourly rate are visible to admins only.</div>
          )}
        </div>
      )}
    </>
  )

  return {
    eyebrow: def.label,
    eyebrowIcon: <EntryIconSvg icon={def.icon} size={13} />,
    pills,
    title: activity.action,
    subtitle: `${who} · ${fmtWhen(activity.createdAt)}`,
    closeRef,
    footerLeft,
    footerRight,
    body,
  }
}
